<script lang="ts">
  /**
   * THE NPC DATABASE — npc.megacity.online (the Chair 10-06: "Let's make the soonami console part of the provisioned agents from MCO
   * (per users agents)"; his Free Guy frames are the spec). A MegaCity person signs in and sees THEIR NPCs: their own (their avatar)
   * and every agent they sponsor (and any they may view), as cards by class beside the stock classes they were stamped from. A card
   * opens its profile — a stock NPC's DB is one Logic file; an NPC that has been changed shows every change as a file, and each file
   * opens as a window (OPEN ALL: Keys's screen). The tabs: D1 Blueprint · D2 Character_Fn · D3 NPC Tree · D4 Control Panel · D5 Mind.
   */
  import { onMount } from 'svelte'
  import { auth, signIn, signOut, startAuth } from './lib/auth.svelte'
  import { api } from './lib/api'
  import { worldDateText } from './lib/worldTime'
  // (the world's date and time — a world minute is 4 real seconds)
  let wnow = $state(worldDateText())
  $effect(() => { const iv = setInterval(() => (wnow = worldDateText()), 1000); return () => clearInterval(iv) })
  import { fileOf, npcCode, pad, show, stockRecord } from './lib/codes'
  import { KIND_LABEL, type Classes, type Entry, type Rec, type Records } from './lib/types'
  import Card from './lib/Card.svelte'
  import Profile from './lib/Profile.svelte'
  import Cascade, { type Win } from './lib/Cascade.svelte'
  import Blueprint from './lib/panels/Blueprint.svelte'
  import Character from './lib/panels/Character.svelte'
  import Tree from './lib/panels/Tree.svelte'
  import Control from './lib/panels/Control.svelte'
  import Mind from './lib/panels/Mind.svelte'

  type Tab = 'db' | 'd1' | 'd2' | 'd3' | 'd4' | 'd5'
  let classes = $state<Classes | null>(null)
  let records = $state<Records | null>(null)
  let loadError = $state<string | null>(null)
  let rec = $state<Rec | null>(null)          // the NPC open now
  let recIndex = $state(0)
  let profile = $state(false)
  let tab = $state<Tab>('db')
  let wins = $state<Win[]>([])
  let opened = $state<{ key: string; label: string }[]>([])
  let profileFilter = $state('all')
  let search = $state('')
  let clock = $state('')
  let busy = $state(false)

  onMount(() => {
    startAuth()
    const t = setInterval(() => (clock = new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })), 10_000)
    clock = new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    return () => clearInterval(t)
  })
  $effect(() => { if (auth.status === 'authed' && !records && !busy) void load() })
  // the rows follow the host (LIVE badges) — a quiet refresh while signed in
  $effect(() => {
    if (auth.status !== 'authed') return
    const iv = setInterval(() => { if (records) api.records().then((r) => (records = r)).catch(() => {}) }, 20_000)
    return () => clearInterval(iv)
  })

  async function load() {
    busy = true; loadError = null
    try { [classes, records] = await Promise.all([api.classes(), api.records()]) }
    catch (e) { loadError = (e as Error).message }
    finally { busy = false }
  }

  const keyOf = (r: { tenant: number; class: string }) => (r.tenant ? `t${r.tenant}` : `s:${r.class}`)
  const rows = $derived((records?.rows ?? []).filter((r) => (profileFilter === 'all' || r.class === profileFilter) && (!search || `${r.title} ${r.class}`.toLowerCase().includes(search.toLowerCase()))))
  const stock = $derived(classes ? Object.keys(classes.classes).filter((c) => (profileFilter === 'all' || c === profileFilter) && (!search || c.includes(search.toLowerCase()))) : [])
  const homeTag = $derived(rec?.effective.home ? rec.effective.home.split('/').filter(Boolean).pop()!.replace(/[^A-Za-z0-9]+/g, '_').replace(/^\w/, (c) => c.toUpperCase()) : 'Unplaced')
  const myCode = $derived(records ? `MC${pad(records.own, 3)}` : 'MC---')

  async function open(tenant: number, cls: string, index: number) {
    recIndex = index
    const key = tenant ? `t${tenant}` : `s:${cls}`
    try {
      rec = tenant ? await api.record(tenant) : stockRecord(cls, classes!)
      profile = true; tab = 'db'
      if (!opened.some((o) => o.key === key)) opened = [...opened.slice(-3), { key, label: tenant ? npcCode(tenant) : `STOCK_${cls.toUpperCase()}` }]
    } catch (e) { loadError = (e as Error).message }
  }
  function reopen(key: string) {
    if (key.startsWith('t')) { const t = Number(key.slice(1)); const i = (records?.rows ?? []).findIndex((r) => r.tenant === t); void open(t, '', i) }
    else { const c = key.slice(2); void open(0, c, (records?.rows.length ?? 0) + Object.keys(classes!.classes).indexOf(c)) }
  }
  function closeOpened(key: string) { opened = opened.filter((o) => o.key !== key); if (rec && keyOf({ tenant: rec.tenant, class: rec.record.class }) === key) { rec = null; profile = false; tab = 'db' } }

  // ── the windows (Keys's cascade) ──
  const entryWin = (e: Entry): Win => {
    const key = e.fn.split('.').slice(e.fn.startsWith('Character_Fn.drives.') ? 2 : 1).join('.')
    return { id: `e${e.id}`, d1: KIND_LABEL[e.kind], d2: e.fn, lines: [
      { t: 'head', s: `revision ${e.revision} · ${e.author} · ${new Date(e.at).toLocaleString()}` },
      { t: 'ctx', s: `${fileOf(e).name}.${fileOf(e).ext}` },
      ...(e.before !== null && e.before !== undefined ? [{ t: 'del' as const, s: `${key} = ${show(e.before)}` }] : []),
      ...(e.after !== null && e.after !== undefined ? [{ t: 'add' as const, s: `${key} = ${show(e.after)}` }] : [{ t: 'del' as const, s: `${key} — dropped` }]),
    ] }
  }
  const pushWin = (w: Win) => { wins = [...wins.filter((x) => x.id !== w.id), w] }
  function openAll() { if (rec) wins = [...rec.journal].reverse().slice(-40).map(entryWin) }
  function logicWin() {
    if (!rec) return
    const t = rec.record.template
    pushWin({ id: 'logic', d1: 'File Logic System', d2: 'Logic', lines: [
      { t: 'head', s: 'the stock it was stamped from — its class: the kit\'s graph + the class\'s role' },
      { t: 'ctx', s: `class   = ${rec.record.class}` }, { t: 'ctx', s: `kit     = ${t.kit ?? '?'}@${t.version ?? '?'}` }, { t: 'ctx', s: `graph   = ${t.graph ?? '?'}` },
      ...JSON.stringify(t.role ?? null, null, 1).split('\n').map((s) => ({ t: 'ctx' as const, s })),
    ] })
  }
  function memoryWin() {
    if (!rec) return
    const m = rec.memories
    pushWin(m.recent ? { id: 'mem', d1: 'Additional Entries', d2: 'memory.db', rows: [['id', 'topic', 'when'], ...m.recent.map((x) => [String(x.id), x.topic ?? '—', x.at ? new Date(x.at).toLocaleDateString() : ''])] }
      : { id: 'mem', d1: 'Additional Entries', d2: 'memory.db', lines: [{ t: 'head', s: `${m.count} memories — as its sponsor you see how many; its topics are its own (a read grant shows them)` }] })
  }
  function roleWin() {
    if (!rec) return
    pushWin({ id: 'role', d1: 'Readout', d2: '_RunsAs.role', lines: [{ t: 'head', s: 'what its host merges into the Citizen graph (simulacrum.role.v1)' }, ...JSON.stringify(rec.role, null, 1).split('\n').map((s) => ({ t: 'ctx' as const, s }))] })
  }

  async function save(b: { revision: number; class?: string; inputs: import('./lib/types').Inputs }): Promise<string> {
    if (!rec) throw new Error('no NPC open')
    const before = rec.journal.length
    const next = await api.save(rec.tenant, b)
    const made = next.journal.length - before
    rec = next
    records = await api.records()
    const p = next.pushed
    const mco = !p ? '' : p.ok ? (p.updated?.length ? ` · pushed to MCO: ${p.updated.join(', ').replace('work_title', 'work title').replace('work_place', 'work place').replace('bio', 'biography')}` : '')
      + (p.skipped && Object.keys(p.skipped).length ? ` · kept on MCO: ${Object.entries(p.skipped).map(([k, v]) => (v === 'custom_picture_kept' ? 'its own picture' : `${k} (${v})`)).join(', ')}` : '')
      : ` · MCO not updated (${p.error ?? 'failed'})`
    return (made ? `Revision ${next.record.revision} — ${made} change${made === 1 ? '' : 's'} written to its DB` : 'Nothing changed — no new revision') + mco
  }
  /** the open record read again (a request decided on D5 changes its mind on the server) */
  async function reloadRec() { if (rec?.tenant) { rec = await api.record(rec.tenant); records = await api.records() } }
  const TABS: [Tab, string, string][] = [['d1', 'D1', ''], ['d2', 'D2', 'Character_Fn'], ['d3', 'D3', 'NPC Tree'], ['d4', 'D4', 'Control Panel'], ['d5', 'D5', 'Mind']]
</script>

{#if auth.status !== 'authed'}
  <main class="gate">
    <div class="gbox frame">
      <p class="gcode mono">MEGACITY · NPC DATABASE</p>
      <h1 class="caps">NPC Database</h1>
      <p class="muted">Your NPCs live here: your own avatar and every agent you sponsor. Each record holds the stock it was stamped from and every change made to it since.</p>
      <button class="btn solid" onclick={signIn} disabled={auth.status === 'pending'}>{auth.status === 'pending' ? 'Waiting for MegaCity…' : 'Sign in with MegaCity'}</button>
      {#if auth.status === 'expired'}<p class="warn">Your sign-in expired. Sign in again.</p>{/if}
      {#if auth.error}<p class="warn">{auth.error}</p>{/if}
    </div>
  </main>
{:else}
  <div class="screen">
    <aside class="rail" aria-label="Tools">
      {#each [['◇', 'Select'], ['⌂', 'Home'], ['◭', 'Play'], ['↶', 'Undo'], ['▤', 'Database'], ['✎', 'Edit'], ['▣', 'Cards'], ['◎', 'Readout'], ['☰', 'Log']] as [g, l], i}
        <button class:on={i === 4} title={l} aria-label={l} onclick={() => { if (i === 4) { profile = false; tab = 'db' } }}>{g}</button>
      {/each}
    </aside>
    <div class="main">
      <header class="top">
        <div class="brand"><span class="mark" aria-hidden="true">⬡</span><span class="mono">NPC DATABASE</span></div>
        <nav class="menu mono"><span class="dtab"><b>00</b></span>
          <button onclick={() => void load()}>File ↻</button><button onclick={signOut}>Sign out</button>
        </nav>
        <nav class="tabs" aria-label="Record tabs">
          {#each TABS as [t, n, label]}
            <button class="dtab" class:active={tab === t} disabled={!rec} onclick={() => { tab = t; profile = false }}>
              <b>{n}</b><span class="dot"></span><span class="n">{t === 'd1' ? `Blueprint_${homeTag}_Obj` : label}</span>
            </button>
          {/each}
        </nav>
        <div class="find">
          <input placeholder="⌕ Search" bind:value={search} aria-label="Search NPCs" />
          <select bind:value={profileFilter} aria-label="Class profile">
            <option value="all">Class Profile — all</option>
            {#each Object.entries(classes?.classes ?? {}) as [k, c]}<option value={k}>{c.title}</option>{/each}
          </select>
        </div>
      </header>
      <div class="title"><span class="dtab"><b>03</b></span><span class="cube" aria-hidden="true">⬢</span><h1 class="caps">NPC Database</h1><span class="code">{myCode}</span>
        <span class="wclock mono" title="The world's date and time — 15 world hours a real hour, a month 2 real days, a year 24">{wnow}</span>
        {#if auth.identity}<span class="who muted mono">{auth.identity.displayName ?? auth.identity.subject}</span>{/if}</div>

      <section class="stage">
        {#if loadError}<p class="err frame">{loadError} <button class="btn" onclick={() => void load()}>Try again</button></p>{/if}
        {#if records?.mco === 'unreachable'}<p class="err frame">MegaCity profiles could not be read just now — every NPC is shown; names may be stale.</p>{/if}
        {#if tab === 'db'}
          <div class="grid scroll" aria-label="NPCs">
            {#if busy && !records}<p class="muted">Reading your NPCs…</p>{/if}
            {#each rows as r, i (r.tenant)}
              <Card title={r.title} cls={r.class} body={r.body} index={i} files={Object.values(r.folders).reduce((a, b) => a + (b ?? 0), 0) + (r.memories ? 1 : 0)}
                badge={r.relation === 'own' ? 'YOU' : r.relation === 'sponsor' ? 'AGENT' : 'VIEW'} live={r.live ? r.place ?? 'its home' : null} selected={rec?.tenant === r.tenant && profile}
                onopen={() => void open(r.tenant, r.class, i)} />
            {/each}
            {#if records && !records.rows.length}<p class="muted">No NPCs yet — sponsor an agent on MegaCity and it appears here.</p>{/if}
            {#if stock.length}<div class="divider mono">STOCK · {classes?.kit.kit}@{classes?.kit.version} — the classes every NPC is stamped from</div>{/if}
            {#each stock as c, i (c)}
              <Card title={classes!.classes[c].title} cls={c} body={classes!.classes[c].body} index={rows.length + i} files={0}
                selected={!!rec && rec.tenant === 0 && rec.record.class === c && profile} onopen={() => void open(0, c, rows.length + i)} />
            {/each}
          </div>
          {#if profile && rec}
            <div class="pwrap"><Profile {rec} index={recIndex} onclose={() => (profile = false)} onentry={(e) => pushWin(entryWin(e))} onall={openAll} onlogic={logicWin} onmemory={memoryWin} onrole={roleWin} /></div>
          {/if}
        {:else if rec && classes}
          <div class="tabpanel scroll">
            {#if tab === 'd1'}<Blueprint {rec} index={recIndex} />{:else if tab === 'd2'}<Character {rec} />{:else if tab === 'd3'}<Tree {rec} />{:else if tab === 'd5'}<Mind {rec} onsave={save} onchanged={reloadRec} />{:else}<Control {rec} {classes} onsave={save} />{/if}
          </div>
        {/if}
        <Cascade {wins} onclose={(id) => (wins = wins.filter((w) => w.id !== id))} onclear={() => (wins = [])} />
      </section>

      <footer class="bottom">
        <button class="dtab" class:active={tab === 'db' && !profile} onclick={() => { profile = false; tab = 'db' }}><b>D1</b><span class="dot"></span><span class="n">DB_{pad(records?.own ?? 0, 4)} <span class="cy">NPC DATABASE</span></span></button>
        {#each opened as o, i (o.key)}
          <span class="dtab opened" class:active={!!rec && keyOf({ tenant: rec.tenant, class: rec.record.class }) === o.key}>
            <button onclick={() => reopen(o.key)}><b>D{i + 2}</b><span class="dot"></span><span class="n">{o.label}</span></button>
            <button class="xx" onclick={() => closeOpened(o.key)} aria-label={`Close ${o.label}`}>×</button>
          </span>
        {/each}
        <span class="spacer"></span>
        <span class="mono muted">ENG · {clock}</span>
      </footer>
    </div>
  </div>
{/if}

<style>
  .gate { min-height: 100%; display: grid; place-items: center; padding: 16px; background: radial-gradient(ellipse at 50% 30%, #0f1a22, var(--bg) 70%); }
  .gbox { max-width: 460px; padding: 28px; background: var(--panel); display: flex; flex-direction: column; gap: 12px; }
  .gcode { color: var(--cyan); margin: 0; font-size: 11px; letter-spacing: .1em; }
  .gbox h1 { margin: 0; font-size: 46px; line-height: 1; color: var(--cyan); }
  .warn { color: var(--amber); margin: 0; }
  .screen { display: grid; grid-template-columns: 46px 1fr; height: 100%; }
  .rail { display: flex; flex-direction: column; align-items: center; gap: 14px; padding: 18px 0; border-right: 1px solid var(--line-dim); background: #080b0f; }
  .rail button { color: var(--muted); font-size: 17px; width: 32px; height: 30px; }
  .rail button.on { color: #001014; background: var(--cyan2); }
  .main { display: flex; flex-direction: column; min-width: 0; min-height: 0; }
  .top { display: flex; align-items: center; gap: 18px; padding: 8px 14px 4px; border-bottom: 1px solid var(--line-dim); background: linear-gradient(180deg, #0b1218, var(--bg)); flex-wrap: wrap; }
  .brand { display: flex; align-items: center; gap: 8px; color: var(--cyan); font-size: 11px; letter-spacing: .1em; }
  .mark { font-size: 20px; }
  .menu { display: flex; gap: 12px; align-items: baseline; color: var(--muted); }
  .menu button:hover { color: var(--text); }
  .tabs { display: flex; gap: 22px; flex: 1; min-width: 0; overflow-x: auto; }
  .tabs .dtab { border-bottom: 2px solid transparent; padding-bottom: 3px; }
  .tabs .dtab.active { border-bottom-color: var(--cyan); }
  .tabs .dtab:disabled { opacity: .35; cursor: default; }
  .find { display: flex; gap: 8px; }
  .find input { width: 140px; }
  .title { display: flex; align-items: baseline; gap: 14px; padding: 10px 16px 6px; }
  .title h1 { margin: 0; font-size: 34px; font-weight: 700; color: var(--cyan); letter-spacing: .01em; }
  .cube { color: var(--text); font-size: 20px; }
  .code { font-size: 22px; color: var(--muted); letter-spacing: .04em; }
  .who { font-size: 11px; }
  .wclock { margin-left: auto; font-size: 12px; color: #9fe8f5; letter-spacing: .03em; }
  .stage { position: relative; flex: 1; min-height: 0; }
  .grid { position: absolute; inset: 0; overflow: auto; padding: 8px 16px 16px; display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 12px; align-content: start; }
  .divider { grid-column: 1 / -1; color: var(--muted); border-top: 1px solid var(--line-dim); padding-top: 8px; margin-top: 6px; font-size: 11px; letter-spacing: .06em; }
  .pwrap { position: absolute; inset: 10px 16px; display: flex; align-items: flex-start; justify-content: center; pointer-events: none; }
  .pwrap > :global(*) { pointer-events: auto; width: min(1180px, 100%); max-height: 100%; }
  .tabpanel { position: absolute; inset: 0; overflow: auto; }
  .err { margin: 8px 16px; padding: 8px 12px; color: var(--red); background: var(--panel); position: relative; z-index: 5; }
  .bottom { display: flex; align-items: center; gap: 22px; padding: 6px 14px; border-top: 1px solid var(--line-dim); background: #080b0f; overflow-x: auto; }
  .bottom .dtab b { font-size: 18px; }
  .bottom .active { color: var(--text); }
  .cy { color: var(--cyan); }
  .opened { display: inline-flex; gap: 4px; }
  .opened button { display: inline-flex; gap: 6px; align-items: baseline; color: inherit; }
  .xx { color: var(--faint); }
  .spacer { flex: 1; }
  @media (max-width: 760px) {
    .screen { grid-template-columns: 1fr; }
    .rail { display: none; }
    .grid { grid-template-columns: repeat(auto-fill, minmax(118px, 1fr)); padding: 8px 12px; gap: 10px; }
    .title h1 { font-size: 26px; }
    .find input { width: 110px; }
    .tabs { order: 3; flex-basis: 100%; gap: 16px; padding-bottom: 2px; }
    .pwrap { inset: 0; }
  }
</style>
